import React, { useMemo, useRef, useState } from 'react';
import { Card, Form, Input, Button, Table, InputNumber, App, DatePicker, Select, Switch, Row, Col, Space } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { purchaseOrderService, type PurchaseOrderDTO, type PurchaseOrderItemDTO } from '@/services/purchaseOrder.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { uiPlainCurrencyLabel } from '@/constants/uaDirhamDisplay';
import { headerProjectLabel, linesCarryProjects } from '@/utils/projectDisplay';
import { useItems } from '@/hooks/useInventory';
import { useProjectsForSelection } from '@/hooks/useProject';
import type { Item } from '@/services/inventory.service';
import { ItemSelect, formatCatalogItemDescription } from '@/components/inventory/ItemSelect';
import { lineSpecificationsExpandable } from '@/components/procurement/LineSpecifications';
import {
  CriticalItemTag,
  getCriticalItemRowClassName,
  isItemCritical,
} from '@/components/inventory/criticalItemUi';
import { useCriticalItemLookup } from '@/hooks/useCriticalItemLookup';
import ProcurementAttachmentsCard from '@/components/procurement/ProcurementAttachmentsCard';
import { DOCUMENT_ENTITY_TYPE_PURCHASE_ORDER } from '@/constants/document-entity-types';
import PoMilestoneScheduleEditor, { type MilestoneRowData } from '@/components/procurement/PoMilestoneScheduleEditor';
import PoOverheadEditor, { type PoOverheadDraft } from '@/components/procurement/PoOverheadEditor';
import { CostControlLineCell } from '@/components/project/CostControlLineCell';
import type { PoMilestoneInput } from '@/services/poMilestone.service';
import { PO_PAYMENT_TERM_MILESTONE, PO_PAYMENT_TERM_STANDARD } from '@/constants/poMilestoneTypes';
import {
  PO_TYPE_OPTIONS,
  PO_TYPE_STANDARD,
  isEnvelopePoType,
} from '@/constants/purchaseOrderTypes';
import { useAuthStore } from '@/store/authStore';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { computePoDocumentGrandTotal } from '@/utils/purchaseOrderTotals';

const { TextArea } = Input;

const TAX_TYPE_OPTIONS = [
  { label: 'VAT 5%', value: 'VAT_5', vat_percentage: 5, vat_applicable: true },
  { label: 'VAT 0%', value: 'VAT_0', vat_percentage: 0, vat_applicable: true },
  { label: 'Exempt', value: 'EXEMPT', vat_percentage: 0, vat_applicable: false },
] as const;

function getTaxTypeValue(vat_percentage: number | undefined, vat_applicable: boolean | undefined): string {
  if (vat_applicable === false) return 'EXEMPT';
  if (vat_percentage === 0) return 'VAT_0';
  return 'VAT_5';
}

function round2(n: number): number {
  return Math.round((Number(n) || 0) * 100) / 100;
}

const initialLine: Partial<PurchaseOrderItemDTO> = {
  item_code: '',
  item_description: '',
  ordered_quantity: 1,
  uom: 'EA',
  project_id: '',
  wbs_node_id: null,
  cbs_code_id: null,
  cost_category: 'Material',
  unit_price: 0,
  discount_percentage: 0,
  vat_percentage: 5,
  vat_applicable: true,
};

/** Match backend calculateTotals for advance % cap (per-line VAT, not flat 5%). */
function computePoGrandTotal(
  lines: Partial<PurchaseOrderItemDTO>[],
  po: Pick<PurchaseOrderDTO, 'discount_percentage' | 'freight_charges' | 'other_charges' | 'total_overhead'>,
  overheadTotal = 0
): number {
  return computePoDocumentGrandTotal(lines, po, overheadTotal);
}

const PurchaseOrderEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [items, setItems] = useState<Partial<PurchaseOrderItemDTO>[]>([]);
  const [overheads, setOverheads] = useState<PoOverheadDraft[]>([]);
  const [paymentTermType, setPaymentTermType] = useState(PO_PAYMENT_TERM_STANDARD);
  const [milestoneRows, setMilestoneRows] = useState<MilestoneRowData[]>([]);
  const { user } = useAuthStore();
  const { isCriticalCode } = useCriticalItemLookup();
  const isSuperAdmin = !!user?.is_super_admin;
  const perms = user?.permissions || [];
  const canManageOverhead =
    isSuperAdmin ||
    perms.includes('procurement.overhead.manage') ||
    perms.includes('procurement.po.edit') ||
    perms.includes('procurement.po.create');
  const canViewOverhead =
    canManageOverhead ||
    perms.includes('procurement.overhead.view') ||
    perms.includes('procurement.po.view');
  const { baseCurrency, allCurrencies, loading: currenciesLoading } = useCurrency();
  const { data: inventoryItems = [], isLoading: inventoryItemsLoading } = useItems({ is_active: true });
  const { data: projects = [] } = useProjectsForSelection();
  const hydratedPoId = useRef<string | null>(null);
  const projectOptions = useMemo(
    () =>
      projects.map((p: { id: string; project_name?: string; project_number?: string }) => ({
        value: p.id,
        label: p.project_name ?? p.project_number ?? p.id,
      })),
    [projects]
  );
  const advanceRequired = Form.useWatch('advance_required', form);
  const advanceType = Form.useWatch('advance_type', form);
  const advancePct = Form.useWatch('advance_payment_percentage', form);
  const poTypeWatch = Form.useWatch('po_type', form);
  const isEnvelope = isEnvelopePoType(poTypeWatch);

  const { data: po, isLoading } = useQuery({
    queryKey: ['purchase-order', id],
    queryFn: () => purchaseOrderService.getById(id!),
    enabled: !!id,
  });

  const currencyOptions = useMemo(() => {
    const active = (allCurrencies || []).filter((c) => c.is_active);
    let opts: { value: string; label: string }[];
    if (active.length > 0) {
      opts = active.map((c) => {
        const d = uiPlainCurrencyLabel(c.symbol, c.currency_code);
        const label =
          d === c.currency_code ? `${c.currency_code} (${c.currency_name})` : `${d} ${c.currency_code}`;
        return { value: c.currency_code, label };
      });
    } else {
      const base = baseCurrency?.currency_code ?? 'AED';
      opts = [{ value: base, label: baseCurrency?.currency_name ? `${base} (${baseCurrency.currency_name})` : base }];
    }
    const current = po?.currency?.trim();
    if (current && !opts.some((o) => o.value === current)) {
      opts = [{ value: current, label: current }, ...opts];
    }
    return opts;
  }, [allCurrencies, baseCurrency, po?.currency]);

  const { data: poItems = [], isLoading: itemsLoading } = useQuery({
    queryKey: ['purchase-order-items', id],
    queryFn: () => purchaseOrderService.getItems(id!),
    enabled: !!id,
  });

  React.useEffect(() => {
    if (po) {
      setPaymentTermType(po.payment_term_type ?? PO_PAYMENT_TERM_STANDARD);
      if (po.milestones?.length) {
        setMilestoneRows(
          po.milestones.map((m, idx) => ({
            key: `ms-${m.id || idx}`,
            id: m.id,
            milestone_number: m.milestone_number ?? idx + 1,
            milestone_name: m.milestone_name,
            description: m.description ?? undefined,
            milestone_type: m.milestone_type,
            fulfillment_method: m.fulfillment_method,
            percentage: Number(m.percentage),
            milestone_amount: Number(m.milestone_amount),
            expected_date: m.expected_date ?? undefined,
            trigger_condition: m.trigger_condition ?? undefined,
            supporting_deliverable: m.supporting_deliverable ?? undefined,
            invoice_requirement: m.invoice_requirement ?? undefined,
            tax_applicable: m.tax_applicable,
            retention_applicable: m.retention_applicable,
            retention_percentage: Number(m.retention_percentage ?? 0),
            remarks: m.remarks ?? undefined,
          }))
        );
      } else {
        setMilestoneRows([]);
      }
      form.setFieldsValue({
        currency: po.currency ?? 'AED',
        notes: po.notes ?? '',
        payment_terms: po.payment_terms ?? '',
        delivery_terms: po.delivery_terms ?? '',
        terms_and_conditions: po.terms_and_conditions ?? '',
        special_instructions: po.special_instructions ?? '',
        delivery_date: po.delivery_date ? dayjs(po.delivery_date) : null,
        delivery_to: po.delivery_to ?? '',
        delivery_address: po.delivery_address ?? '',
        department_id: po.department_id ?? undefined,
        po_type: po.po_type || PO_TYPE_STANDARD,
        agreement_start_date: po.agreement_start_date ? dayjs(po.agreement_start_date) : null,
        agreement_end_date: po.agreement_end_date ? dayjs(po.agreement_end_date) : null,
        max_value: po.max_value != null ? Number(po.max_value) : undefined,
        advance_required: !!po.advance_required,
        advance_type: po.advance_type ?? 'Percentage',
        advance_payment_percentage: po.advance_payment_percentage ?? 0,
        advance_payment_amount: po.advance_payment_amount ?? 0,
        advance_reason: po.advance_reason ?? '',
        expected_advance_date: po.expected_advance_date ? dayjs(po.expected_advance_date) : null,
        advance_payment_terms: po.advance_payment_terms ?? '',
        advance_approval_required: !!po.advance_approval_required,
      });
      setOverheads(
        Array.isArray(po.overheads)
          ? po.overheads.map((oh) => ({
              ...oh,
              original_amount: Number(oh.original_amount ?? oh.amount) || 0,
              amount: Number(oh.amount ?? oh.original_amount) || 0,
            }))
          : []
      );
    }
  }, [po, form]);

  React.useEffect(() => {
    if (!id || itemsLoading) return;
    if (hydratedPoId.current === id) return;
    hydratedPoId.current = id;
    setItems(
      poItems.length
        ? poItems.map((i) => ({
            id: i.id,
            line_number: i.line_number,
            item_code: i.item_code,
            item_description: i.item_description,
            supplier_description: i.supplier_description || i.item_description,
            ordered_quantity: Number(i.ordered_quantity),
            uom: i.uom || 'EA',
            unit_price: Number(i.unit_price),
            discount_percentage: i.discount_percentage != null ? Number(i.discount_percentage) : 0,
            vat_percentage: i.vat_percentage != null ? Number(i.vat_percentage) : 5,
            vat_applicable: i.vat_applicable !== false,
            project_id: i.project_id || i.project?.id || '',
            wbs_node_id: i.wbs_node_id ?? null,
            cbs_code_id: i.cbs_code_id ?? null,
            cost_category: i.cost_category || 'Material',
            specifications: i.specifications ?? '',
          }))
        : [{ ...initialLine }]
    );
  }, [id, itemsLoading, poItems]);

  const overheadTotal = React.useMemo(
    () => round2(overheads.reduce((s, o) => s + Number(o.amount ?? o.original_amount ?? 0), 0)),
    [overheads]
  );

  const poGrandTotal = React.useMemo(
    () => (po ? computePoGrandTotal(items, po, overheadTotal) : 0),
    [items, po, overheadTotal]
  );

  const syncAdvanceAmountFromPercentage = React.useCallback(
    (pct: number | null | undefined, type: string | undefined) => {
      if (!po || type !== 'Percentage') return;
      const p = Number(pct) || 0;
      if (p <= 0 || poGrandTotal <= 0) return;
      form.setFieldValue('advance_payment_amount', round2((poGrandTotal * p) / 100));
    },
    [form, po, poGrandTotal]
  );

  React.useEffect(() => {
    if (advanceRequired && advanceType === 'Percentage') {
      syncAdvanceAmountFromPercentage(advancePct, advanceType);
    }
  }, [advanceRequired, advanceType, advancePct, poGrandTotal, syncAdvanceAmountFromPercentage]);

  const updateMutation = useMutation({
    mutationFn: (payload: {
      po: Partial<PurchaseOrderDTO>;
      items: Partial<PurchaseOrderItemDTO>[];
      milestones?: PoMilestoneInput[];
      overheads?: PoOverheadDraft[];
    }) => purchaseOrderService.update(id!, payload),
    onSuccess: () => {
      message.success('Purchase order updated');
      queryClient.invalidateQueries({ queryKey: ['purchase-order', id] });
      queryClient.invalidateQueries({ queryKey: ['purchase-order-items', id] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      navigate(`/scm/procurement/purchase-orders/${id}`);
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || 'Update failed');
    },
  });

  const addLine = () => {
    setItems((prev) => [...prev, { ...initialLine }]);
  };

  const removeLine = (index: number) => {
    if (items.length <= 1) return;
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const onSelectInventoryItem = (index: number, itemId: string) => {
    const item = inventoryItems.find((i: Item) => i.id === itemId);
    if (!item) return;
    setItems((prev) => {
      const next = [...prev];
      const row = next[index];
      if (row) {
        row.item_code = item.item_code ?? '';
        row.item_description = formatCatalogItemDescription(item);
        row.uom = item.purchase_uom || item.base_uom || 'EA';
        row.unit_price = Number(item.last_purchase_price) || Number(item.standard_cost) || 0;
        const pct = item.vat_percentage != null ? Number(item.vat_percentage) : 5;
        row.vat_percentage = pct;
        row.vat_applicable = item.vat_applicable ?? true;
        (row as { is_critical?: boolean }).is_critical = isItemCritical(item);
        if (!String(row.specifications ?? '').trim() && item.specifications?.trim()) {
          row.specifications = item.specifications;
        }
      }
      return next;
    });
  };

  const onFinish = () => {
    const values = form.getFieldsValue();
    const poPayload = {
      currency: values.currency,
      notes: values.notes,
      payment_terms: values.payment_terms,
      delivery_terms: values.delivery_terms,
      terms_and_conditions: values.terms_and_conditions,
      special_instructions: values.special_instructions,
      po_type: values.po_type || PO_TYPE_STANDARD,
      delivery_date: values.delivery_date ? values.delivery_date.format('YYYY-MM-DD') : undefined,
      delivery_to: values.delivery_to,
      delivery_address: values.delivery_address,
      department_id: values.department_id,
      agreement_start_date: values.agreement_start_date
        ? values.agreement_start_date.format('YYYY-MM-DD')
        : null,
      agreement_end_date: values.agreement_end_date
        ? values.agreement_end_date.format('YYYY-MM-DD')
        : null,
      max_value: values.max_value != null ? Number(values.max_value) : null,
      advance_required: !!values.advance_required,
      advance_type: values.advance_type,
      advance_payment_percentage: Number(values.advance_payment_percentage) || 0,
      advance_payment_amount:
        values.advance_type === 'Percentage' && Number(values.advance_payment_percentage) > 0 && po
          ? round2((computePoGrandTotal(items, po, overheadTotal) * Number(values.advance_payment_percentage)) / 100)
          : Number(values.advance_payment_amount) || 0,
      advance_reason: values.advance_reason,
      expected_advance_date: values.expected_advance_date
        ? values.expected_advance_date.format('YYYY-MM-DD')
        : undefined,
      advance_payment_terms: values.advance_payment_terms,
      advance_approval_required: !!values.advance_approval_required,
      payment_term_type: paymentTermType,
    };
    const milestones =
      paymentTermType === PO_PAYMENT_TERM_MILESTONE
        ? milestoneRows.map((m, i) => ({
            id: m.id,
            milestone_number: i + 1,
            milestone_name: m.milestone_name,
            description: m.description,
            milestone_type: m.milestone_type,
            fulfillment_method: m.fulfillment_method,
            percentage: m.percentage,
            milestone_amount: m.milestone_amount,
            expected_date: m.expected_date,
            trigger_condition: m.trigger_condition,
            supporting_deliverable: m.supporting_deliverable,
            invoice_requirement: m.invoice_requirement,
            tax_applicable: m.tax_applicable,
            retention_applicable: m.retention_applicable,
            retention_percentage: m.retention_percentage,
            remarks: m.remarks,
          }))
        : [];
    const itemsPayload = items
      .map((it, i) => {
        const row: Partial<PurchaseOrderItemDTO> = {
          line_number: i + 1,
          item_code: it.item_code || undefined,
          item_description: String(it.item_description ?? '').trim(),
          supplier_description: String(it.supplier_description ?? it.item_description ?? '').trim(),
          ordered_quantity: Number(it.ordered_quantity) || 0,
          uom: it.uom || 'EA',
          unit_price: Number(it.unit_price) ?? 0,
          discount_percentage: Number(it.discount_percentage) ?? 0,
          vat_percentage: Number(it.vat_percentage) ?? 5,
          vat_applicable: it.vat_applicable !== false,
          project_id: it.project_id || undefined,
          wbs_node_id: it.wbs_node_id || null,
          cbs_code_id: it.cbs_code_id || null,
          cost_category: it.cost_category || 'Material',
          specifications: String(it.specifications ?? '').trim() || null,
        };
        if (it.id) row.id = it.id;
        return row;
      })
      .filter(
        (l) =>
          Boolean(l.item_description) &&
          (Number(l.ordered_quantity) || 0) > 0 &&
          (Number(l.unit_price) ?? 0) >= 0
      )
      .map((row, i) => ({ ...row, line_number: i + 1 }));
    if (itemsPayload.length === 0) {
      message.error('Add at least one line with description, quantity and unit price');
      return;
    }
    const distinctProjects = [
      ...new Set(itemsPayload.map((it) => it.project_id).filter((id): id is string => Boolean(id))),
    ];
    updateMutation.mutate({
      po: {
        ...poPayload,
        project_id: distinctProjects.length === 1 ? distinctProjects[0] : null,
      },
      items: itemsPayload,
      milestones: paymentTermType === PO_PAYMENT_TERM_MILESTONE ? milestones : [],
      overheads: canManageOverhead
        ? overheads.map((oh, i) => ({
            line_number: i + 1,
            overhead_type: oh.overhead_type || 'Other',
            description: oh.description,
            remarks: oh.remarks,
            currency: oh.currency || values.currency || po?.currency,
            exchange_rate: Number(oh.exchange_rate) || 1,
            original_amount: Number(oh.original_amount ?? oh.amount) || 0,
            amount: Number(oh.amount ?? oh.original_amount) || 0,
          }))
        : undefined,
    });
  };

  const updateItem = (index: number, field: keyof PurchaseOrderItemDTO, value: unknown) => {
    setItems((prev) => {
      const next = [...prev];
      const row = next[index];
      if (!row) return next;
      if (field === 'item_description') {
        const current = String(row.item_description ?? '');
        const supplier = String(row.supplier_description ?? '');
        row.item_description = String(value ?? '');
        if (!supplier || supplier === current) row.supplier_description = String(value ?? '');
      } else {
        (row as any)[field] = value;
      }
      return next;
    });
  };

  if (!id) return null;
  if (isLoading || itemsLoading) return <Card loading />;
  if (!po) return <Card><p>Purchase order not found.</p></Card>;
  if (!['Draft', 'Submitted'].includes(po.po_status)) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <p>Only Draft or Submitted purchase orders can be edited. Use Create amendment for approved POs.</p>
          <Button type="primary" onClick={() => navigate(`/scm/procurement/purchase-orders/${id}`)}>
            Back to PO
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(`/scm/procurement/purchase-orders/${id}`)} style={{ marginBottom: 16 }}>
        Back
      </Button>
      <Card title={`Edit Purchase Order ${po?.po_number ?? id}`}>
        <Form form={form} layout="vertical" onFinish={onFinish}>
          {linesCarryProjects(items) ? null : (
            <Form.Item label="Project">
              <Input readOnly value={headerProjectLabel(po, items)} />
            </Form.Item>
          )}
          <Form.Item
            name="department_id"
            label="Department"
            rules={[{ required: true, message: 'Department is required' }]}
          >
            <DepartmentSelect />
          </Form.Item>
          <Form.Item
            name="po_type"
            label="PO type"
            rules={[{ required: true, message: 'Select PO type' }]}
            extra={
              isEnvelope
                ? 'Envelope orders hold agreed pricing; create releases/call-offs for delivery after approval.'
                : undefined
            }
          >
            <Select
              options={PO_TYPE_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
                title: o.description,
              }))}
            />
          </Form.Item>
          <Form.Item name="delivery_date" label="Delivery date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          {isEnvelope ? (
            <>
              <Form.Item name="agreement_start_date" label="Agreement start">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="agreement_end_date" label="Agreement end">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="max_value" label="Max value (optional)">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </>
          ) : null}
          <Form.Item name="delivery_to" label="Delivery to">
            <Input />
          </Form.Item>
          <Form.Item name="delivery_address" label="Delivery address">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="currency" label="Currency" rules={[{ required: true, message: 'Select currency' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              loading={currenciesLoading}
              options={currencyOptions}
            />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item
            name="payment_terms"
            label="Payment terms"
            extra="Printed under Terms & Conditions → Payment"
          >
            <TextArea rows={2} placeholder="e.g. Net 30, or agreement reference text" />
          </Form.Item>
          <Form.Item
            name="delivery_terms"
            label="Delivery terms"
            extra="Printed under Terms & Conditions → Delivery (falls back to delivery date if empty)"
          >
            <TextArea rows={2} placeholder="e.g. 15 Days from the Date of Invoice" />
          </Form.Item>
          <Form.Item name="terms_and_conditions" label="Additional terms and conditions">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item name="special_instructions" label="Special instructions">
            <TextArea rows={2} />
          </Form.Item>

          <h4>Payment term type</h4>
          <Form.Item label="Milestone-based payment">
            <Switch
              checked={paymentTermType === PO_PAYMENT_TERM_MILESTONE}
              checkedChildren="Milestone-based"
              unCheckedChildren="Standard"
              onChange={(c) => {
                setPaymentTermType(c ? PO_PAYMENT_TERM_MILESTONE : PO_PAYMENT_TERM_STANDARD);
                if (c && milestoneRows.length === 0) {
                  setMilestoneRows([
                    {
                      key: 'ms-1',
                      milestone_number: 1,
                      milestone_name: '',
                      milestone_type: 'Material Delivery',
                      fulfillment_method: 'GRN',
                      percentage: 100,
                      milestone_amount: poGrandTotal,
                      tax_applicable: true,
                      retention_applicable: false,
                      retention_percentage: 0,
                    },
                  ]);
                }
              }}
            />
          </Form.Item>
          <PoMilestoneScheduleEditor
            rows={milestoneRows}
            onChange={setMilestoneRows}
            grandTotal={poGrandTotal}
            paymentTermType={paymentTermType}
          />

          <h4>Supplier advance</h4>
          <Form.Item name="advance_required" label="Advance required" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="advance_type" label="Advance type">
                <Select options={[{ value: 'Percentage', label: 'Percentage' }, { value: 'Fixed', label: 'Fixed amount' }]} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="advance_payment_percentage"
                label="Advance %"
                help={
                  advanceType === 'Percentage' && poGrandTotal > 0
                    ? `PO grand total: ${po?.currency ?? ''} ${poGrandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                    : undefined
                }
              >
                <InputNumber
                  min={0}
                  max={100}
                  style={{ width: '100%' }}
                  disabled={advanceType !== 'Percentage'}
                  onChange={(v) => syncAdvanceAmountFromPercentage(v, advanceType ?? 'Percentage')}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="advance_payment_amount"
                label="Advance amount"
                help={advanceType === 'Fixed' ? 'Enter fixed advance (not derived from %)' : undefined}
              >
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  disabled={advanceType === 'Percentage'}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="advance_reason" label="Advance reason">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="expected_advance_date" label="Expected advance date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="advance_payment_terms" label="Advance payment terms">
            <Input />
          </Form.Item>
          <Form.Item name="advance_approval_required" label="Approval required before payment" valuePropName="checked">
            <Switch />
          </Form.Item>

          <h4>Line items (at least one required)</h4>
          <Table
            size="small"
            rowKey={(_, idx) => String(idx)}
            pagination={false}
            dataSource={items}
            scroll={{ x: 1680 }}
            expandable={lineSpecificationsExpandable<Partial<PurchaseOrderItemDTO>>({
              getValue: (r) => r.specifications,
              onChange: (r, v) => updateItem(items.indexOf(r), 'specifications', v),
            })}
            rowClassName={(r) =>
              getCriticalItemRowClassName({
                is_critical:
                  (r as { is_critical?: boolean }).is_critical ?? isCriticalCode(r.item_code),
              })
            }
            columns={[
              { title: '#', width: 44, fixed: 'left', render: (_, __, idx) => idx + 1 },
              {
                title: 'Item (from inventory)',
                width: 240,
                render: (_, __, idx) => (
                  <ItemSelect
                    items={inventoryItems}
                    autoLoad={false}
                    loading={inventoryItemsLoading}
                    placeholder="Select item or enter manually below"
                    style={{ width: '100%', minWidth: 200 }}
                    value={undefined}
                    onChange={(v) => v && onSelectInventoryItem(idx, String(v))}
                  />
                ),
              },
              {
                title: 'Item code',
                width: 160,
                render: (_, __, idx) => {
                  const row = items[idx];
                  const critical =
                    (row as { is_critical?: boolean } | undefined)?.is_critical ??
                    isCriticalCode(row?.item_code);
                  return (
                    <Space size={6} wrap>
                      <Input
                        value={row?.item_code}
                        onChange={(e) => updateItem(idx, 'item_code', e.target.value)}
                        placeholder="Optional"
                        style={{ width: 110 }}
                      />
                      <CriticalItemTag critical={critical} />
                    </Space>
                  );
                },
              },
              {
                title: 'Description',
                dataIndex: 'item_description',
                width: 200,
                render: (val, __, idx) => (
                  <Input
                    value={typeof val === 'string' ? val : items[idx]?.item_description ?? ''}
                    onChange={(e) => updateItem(idx, 'item_description', e.target.value)}
                    placeholder="Required"
                  />
                ),
              },
              {
                title: 'Supplier Description',
                width: 200,
                render: (_, __, idx) => (
                  <Input
                    value={items[idx]?.supplier_description ?? items[idx]?.item_description ?? ''}
                    onChange={(e) => updateItem(idx, 'supplier_description', e.target.value)}
                    placeholder="Shown on the purchase order print"
                  />
                ),
              },
              {
                title: 'Qty',
                dataIndex: 'ordered_quantity',
                width: 100,
                render: (val, __, idx) => (
                  <InputNumber
                    min={0.0001}
                    value={val}
                    onChange={(v) => updateItem(idx, 'ordered_quantity', v)}
                    style={{ width: '100%' }}
                  />
                ),
              },
              {
                title: 'UOM',
                width: 80,
                render: (_, __, idx) => (
                  <Input
                    value={items[idx]?.uom}
                    onChange={(e) => updateItem(idx, 'uom', e.target.value)}
                  />
                ),
              },
              {
                title: 'Unit price',
                dataIndex: 'unit_price',
                width: 110,
                render: (val, __, idx) => (
                  <InputNumber
                    min={0}
                    value={val}
                    onChange={(v) => updateItem(idx, 'unit_price', v)}
                    style={{ width: '100%' }}
                  />
                ),
              },
              {
                title: 'Discount %',
                width: 100,
                render: (_, __, idx) => (
                  <InputNumber
                    min={0}
                    max={100}
                    value={items[idx]?.discount_percentage}
                    onChange={(v) => updateItem(idx, 'discount_percentage', v)}
                    style={{ width: '100%' }}
                  />
                ),
              },
              {
                title: 'Project',
                width: 200,
                render: (_, __, idx) => (
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Project"
                    style={{ width: '100%', minWidth: 160 }}
                    options={projectOptions}
                    value={items[idx]?.project_id || undefined}
                    onChange={(v) => updateItem(idx, 'project_id', v ?? null)}
                  />
                ),
              },
              {
                title: 'Budget / WBS',
                width: 200,
                render: (_, __, idx) => (
                  <CostControlLineCell
                    projectId={items[idx]?.project_id}
                    value={{
                      wbs_node_id: items[idx]?.wbs_node_id,
                      cbs_code_id: items[idx]?.cbs_code_id,
                      cost_category: items[idx]?.cost_category,
                    }}
                    requestedAmount={
                      Number(items[idx]?.ordered_quantity || 0) *
                      Number(items[idx]?.unit_price || 0) *
                      (1 - Number(items[idx]?.discount_percentage || 0) / 100)
                    }
                    onChange={(patch) => {
                      updateItem(idx, 'wbs_node_id', patch.wbs_node_id ?? null);
                      updateItem(idx, 'cbs_code_id', patch.cbs_code_id ?? null);
                      updateItem(idx, 'cost_category', patch.cost_category ?? 'Material');
                    }}
                  />
                ),
              },
              {
                title: 'Tax type',
                width: 120,
                render: (_, __, idx) => (
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Tax type"
                    options={TAX_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                    value={getTaxTypeValue(items[idx]?.vat_percentage, items[idx]?.vat_applicable)}
                    onChange={(value) => {
                      const opt = TAX_TYPE_OPTIONS.find((o) => o.value === value);
                      if (opt) {
                        updateItem(idx, 'vat_percentage', opt.vat_percentage);
                        updateItem(idx, 'vat_applicable', opt.vat_applicable);
                      }
                    }}
                  />
                ),
              },
              {
                title: '',
                width: 50,
                render: (_, __, idx) =>
                  items.length > 1 ? (
                    <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => removeLine(idx)} />
                  ) : null,
              },
            ]}
          />
          <Button type="dashed" onClick={addLine} icon={<PlusOutlined />} style={{ marginTop: 8 }}>
            Add line
          </Button>

          {canViewOverhead ? (
            <PoOverheadEditor
              overheads={overheads}
              onChange={setOverheads}
              currency={po?.currency || 'AED'}
              readOnly={!canManageOverhead}
            />
          ) : null}

          <Form.Item style={{ marginTop: 24 }}>
            <Button type="primary" htmlType="submit" loading={updateMutation.isPending}>
              Save
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => navigate(`/scm/procurement/purchase-orders/${id}`)}>
              Cancel
            </Button>
          </Form.Item>
        </Form>
        <ProcurementAttachmentsCard
          entityType={DOCUMENT_ENTITY_TYPE_PURCHASE_ORDER}
          entityId={id}
          queryKeyPrefix={['documents', 'purchase-order']}
        />
      </Card>
    </div>
  );
};

export default PurchaseOrderEditPage;
